import axios from 'axios';

// Action Types
export const AUTO_SPLIT_FETCH_BEGIN = 'AUTO_SPLIT_FETCH_BEGIN';
export const AUTO_SPLIT_FETCH_SUCCESS = 'AUTO_SPLIT_FETCH_SUCCESS';
export const AUTO_SPLIT_FETCH_FAIL = 'AUTO_SPLIT_FETCH_FAIL';
export const AUTO_SPLIT_DISMISS = 'AUTO_SPLIT_DISMISS';

/**
 * Determines if the split suggestion is valid (actual split, not just one part with all pages)
 * @param {Array} parts - Array of document parts
 * @param {number} totalPages - Total number of pages in the document (optional, calculated from parts if not provided)
 * @returns {boolean}
 */
export const hasValidSplit = (parts, totalPages) => {
  if (!parts || parts.length === 0) return false;
  if (parts.length > 1) return true;
  // Single part - check if it's all pages
  const allPages = parts[0].pages || [];
  if (allPages.length === 0) return false;
  // If totalPages is not provided, calculate from parts
  const calculatedTotalPages = totalPages || Math.max(...allPages);
  return allPages.length !== calculatedTotalPages;
};

// Cache TTL in milliseconds (5 minutes)
const CACHE_TTL = 5 * 60 * 1000;

/**
 * Fetch auto-split suggestions for a received record
 * @param {string} sendToken - The send_token of the received record
 * @param {number} totalPages - Total number of pages in the document (for validation)
 * @param {boolean} forceRefresh - If true, bypass cache and fetch fresh data
 */
export const fetchAutoSplitSuggestions = (
  sendToken,
  totalPages = 0,
  forceRefresh = false,
  requestClient = axios,
) => async (dispatch, getState) => {
  const state = getState();
  const existingSuggestion = state.autoSplit?.suggestions?.[sendToken];
  const isLoading = state.autoSplit?.loading?.[sendToken];

  // Skip fetch if already loading (prevent duplicate requests)
  if (isLoading) {
    return existingSuggestion
      ? { parts: existingSuggestion.parts, hasValidSplit: existingSuggestion.hasValidSplit }
      : { parts: [], hasValidSplit: false };
  }

  // Use cached data if available, not expired, and not forcing refresh
  if (!forceRefresh && existingSuggestion?.fetchedAt) {
    const cacheAge = Date.now() - existingSuggestion.fetchedAt;
    if (cacheAge < CACHE_TTL) {
      return { parts: existingSuggestion.parts, hasValidSplit: existingSuggestion.hasValidSplit };
    }
  }

  dispatch({
    type: AUTO_SPLIT_FETCH_BEGIN,
    payload: { sendToken },
  });

  try {
    const response = await requestClient({
      method: 'GET',
      url: `/api/v1/receivers/${sendToken}/split_suggestions`,
      headers: {
        'X-CSRF-TOKEN': document.getElementsByName('csrf-token')[0]?.content,
      },
    });

    const parts = response.data.parts || [];
    const isValidSplit = hasValidSplit(parts, totalPages);

    dispatch({
      type: AUTO_SPLIT_FETCH_SUCCESS,
      payload: {
        sendToken,
        parts,
        hasValidSplit: isValidSplit,
        fetchedAt: Date.now(),
      },
    });

    return { parts, hasValidSplit: isValidSplit };
  } catch (error) {
    const errorMessage = error.response?.data?.error || 'Failed to fetch split suggestions';

    dispatch({
      type: AUTO_SPLIT_FETCH_FAIL,
      payload: {
        sendToken,
        error: errorMessage,
      },
    });

    return { error: true, message: errorMessage };
  }
};

/**
 * Dismiss the auto-split suggestion for a record (user chose to ignore)
 * @param {string} sendToken
 */
export const dismissAutoSplitSuggestion = (sendToken) => (dispatch) => {
  dispatch({
    type: AUTO_SPLIT_DISMISS,
    payload: { sendToken },
  });
};

// Action types for AI split
export const AI_SPLIT_BEGIN = 'AI_SPLIT_BEGIN';
export const AI_SPLIT_SUCCESS = 'AI_SPLIT_SUCCESS';
export const AI_SPLIT_FAIL = 'AI_SPLIT_FAIL';

/**
 * Perform AI-assisted split with per-part patient info
 * @param {string} sendToken - The send_token of the received record
 * @param {Array} parts - Array of parts with pages and patient info
 *   Each part: { pages: [1,2], title: "...", patient_first_name: "...", patient_last_name: "...", patient_dob: "..." }
 */
export const performAiSplit = (sendToken, parts, requestClient = axios) => async (dispatch) => {
  dispatch({
    type: AI_SPLIT_BEGIN,
    payload: { sendToken },
  });

  try {
    // Format parts for the API
    const formattedParts = parts.map((part) => ({
      pages: part.pages,
      title: part.title || '',
      patient_first_name: part.patients?.[0]?.first_name || '',
      patient_last_name: part.patients?.[0]?.last_name || '',
      patient_middle_name: part.patients?.[0]?.middle_name || '',
      patient_dob: part.patients?.[0]?.dob || '',
    }));

    const response = await requestClient({
      method: 'POST',
      url: `/api/v1/receivers/${sendToken}/ai_split`,
      headers: {
        'X-CSRF-TOKEN': document.getElementsByName('csrf-token')[0]?.content,
      },
      data: { parts: formattedParts },
    });

    dispatch({
      type: AI_SPLIT_SUCCESS,
      payload: { sendToken },
    });

    return { success: true, data: response.data };
  } catch (error) {
    const errorMessage = error.response?.data?.error || 'Failed to split document';

    dispatch({
      type: AI_SPLIT_FAIL,
      payload: { sendToken, error: errorMessage },
    });

    return { success: false, error: errorMessage };
  }
};
